// hooks/useCompanyRole.js
//
// Small hook that resolves the current user's COMPANY role (admin/reviewer/user)
// for UI gating — e.g. locking "Resubmit" for users, showing "Load to Nezo"
// only to reviewers/admins.
//
// IMPORTANT: this is for UI convenience only. The server independently
// re-checks the company role on every mutating route (companyAuth middleware),
// so a tampered client cannot gain privileges.
//
// It reads the roster endpoint GET /connections, which already returns
// { myRole, isAdmin, pendingReceived } for the caller. No extra backend needed.
//
// pendingReceived is surfaced here too so the dashboard can badge an unaccepted
// company invite. Without that, an invitee only ever learns about it from the
// invite email — and if that is missed, they use the console indefinitely
// without company visibility and nothing tells them why.

import { useState, useEffect, useContext } from 'react';
import { auth } from '../firebaseConfig';
import { API_BASE_URL } from '../services/api';
import { AuthContext } from '../context/AuthContext';
import { useRegion } from '../context/RegionContext';

export function useCompanyRole() {
  const { user } = useContext(AuthContext);
  const { selectedRegion } = useRegion();

  const [companyRole, setCompanyRole]       = useState(null);   // 'admin' | 'reviewer' | 'user' | null
  const [pendingInvites, setPendingInvites] = useState([]);     // invites awaiting MY response
  const [loading, setLoading]               = useState(true);
  const [hasCompany, setHasCompany]         = useState(false);  // in ANY company (any role)

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!user) { setLoading(false); return; }
      try {
        const token = await auth.currentUser?.getIdToken();
        const res = await fetch(`${API_BASE_URL}/connections`, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            'x-user-email': user.email || '',
            'x-user-role': user.role || 'user',
            'x-region': selectedRegion || 'vquest',
          },
        });
        const data = await res.json();
        if (!cancelled) {
          setCompanyRole(data.myRole || null);
          setHasCompany(!!data.company);
          setPendingInvites(data.pendingReceived || []);
        }
      } catch (err) {
        console.error('useCompanyRole failed:', err);
        if (!cancelled) {
          setCompanyRole(null);
          setHasCompany(false);
          setPendingInvites([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user, selectedRegion]);

  return {
    companyRole,
    pendingInvites,
    pendingInviteCount: pendingInvites.length,
    loading,
    isCompanyAdmin:    companyRole === 'admin',
    isCompanyReviewer: companyRole === 'reviewer',
    isCompanyUser:     companyRole === 'user',
    // convenience: can this person take reviewer-level actions?
    canReview:         companyRole === 'admin' || companyRole === 'reviewer',
    // Upload straight to Nezo: a company's reviewers/admins, OR anyone not in a company at all (a solo
    // user, or a Nezo user signed in from Nezo — their Nezo account already shares the material with
    // their team). Companies keep their own review rules for their plain members.
    hasCompany,
    canUpload:         companyRole === 'admin' || companyRole === 'reviewer' || (!loading && !hasCompany),
  };
}